import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  AgencySettings_EnabledFeaturesEnum,
  PolicyTimeslot_DaysEnum,
  PolicyRule_VehicleTypesEnum,
  PolicyTimeslot,
} from 'src/api/gen/api'
import { IPolicyRuleDwellTime } from 'src/api/types/policy'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { isEndTimeBeforeStartTime } from 'src/commons/policies/utils'
import { useCheckEnabledFeature } from 'src/layout/AgencySettings'
import SETTINGS from 'src/settings'

import { FieldTitle } from '../../commons'
import { GeographyField } from '../fields'

import TimeSlotForm from './TimeSlot'

function useStyles() {
  return makeStyles(() => ({
    inlineFlexFieldWrapper: { display: 'flex', gap: '1rem', alignItems: 'center' },
    inlineFlexField: { minWidth: '65%' },
    inlineFlexTitleField: { minWidth: '30%' },
  }))()
}

function DwellTimeRule(props: IBaseTableProps<IPolicyRuleDwellTime>) {
  const { t } = useTranslation()
  const classes = useStyles()
  const buffered_areas_enabled = useCheckEnabledFeature()(AgencySettings_EnabledFeaturesEnum.buffered_areas)

  const handleChangeRule =
    (ruleId: string, key: keyof IPolicyRuleDwellTime) =>
    (val: string | string[] | number | PolicyRule_VehicleTypesEnum[] | null): void => {
      const newRule: IPolicyRuleDwellTime[] = props.rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }
        return { ...rule, [key]: val }
      })
      // we are using simple selector for prohibited/authorized zones but we actually still store it in an array for now
      // so we need to update the rule accordingly
      if (!Array.isArray(newRule[0].prohibited_parking_zones)) {
        newRule[0].prohibited_parking_zones = [newRule[0].prohibited_parking_zones ?? '']
      }
      if (!Array.isArray(newRule[0].buffered_prohibited_parking_zones)) {
        newRule[0].buffered_prohibited_parking_zones = [newRule[0].buffered_prohibited_parking_zones ?? '']
      }
      if (!Array.isArray(newRule[0].authorized_parking_zones)) {
        newRule[0].authorized_parking_zones = [newRule[0].authorized_parking_zones ?? '']
      }
      if (!Array.isArray(newRule[0].buffered_authorized_parking_zones)) {
        newRule[0].buffered_authorized_parking_zones = [newRule[0].buffered_authorized_parking_zones ?? '']
      }
      props.handleChange(newRule)
    }

  function handleChangeTimeslot(timeslot: PolicyTimeslot, index: number, key: keyof PolicyTimeslot) {
    return (val: string | PolicyTimeslot_DaysEnum[] | null): void => {
      const timeslotsToUpdate: PolicyTimeslot[] = props.rules[0].timeslots
      timeslotsToUpdate[index] = { ...timeslot, [key]: val }
      const newRules: IPolicyRuleDwellTime[] = [{ ...props.rules[0], ['timeslots']: timeslotsToUpdate }]
      props.handleChange(newRules)
    }
  }

  const {
    rule_id,
    prohibited_parking_zones,
    buffered_prohibited_parking_zones,
    authorized_parking_zones,
    buffered_authorized_parking_zones,
  } = props.rules[0]

  return (
    <>
      <Grid container direction="column" spacing={3} style={{ marginTop: -35 }}>
        <Grid container direction="row" spacing={10} style={{ paddingLeft: '9px' }}>
          <Grid item xs={6} spacing={5}>
            <div className={classes.inlineFlexFieldWrapper}>
              <div className={classes.inlineFlexTitleField}>
                <FieldTitle
                  title={t('titles.prohibited_zones.dwell_time')}
                  tooltip={t('tooltips.prohibited_zones.dwell_time')}
                />
              </div>
              <div className={classes.inlineFlexField}>
                <GeographyField
                  id="dwell_time_base_geographies_selection"
                  value={prohibited_parking_zones[0]}
                  allGeographies={props.allGeographies}
                  handleChange={handleChangeRule(rule_id, 'prohibited_parking_zones')}
                  disabled={props.disabled}
                  helperText={props.validation[0].getFieldErrorMsg('geographies')}
                />
              </div>
            </div>
          </Grid>
          {buffered_areas_enabled ? (
            <Grid item xs={6}>
              <div className={classes.inlineFlexFieldWrapper}>
                <div className={classes.inlineFlexTitleField}>
                  <FieldTitle
                    title={t('titles.buffered_prohibited_zones.dwell_time')}
                    tooltip={t('tooltips.buffered_prohibited_zones.dwell_time')}
                  />
                </div>
                <div className={classes.inlineFlexField}>
                  <GeographyField
                    id="dwell_time_buffer_geographies_selection"
                    value={buffered_prohibited_parking_zones[0]}
                    allGeographies={props.allGeographies}
                    handleChange={handleChangeRule(rule_id, 'buffered_prohibited_parking_zones')}
                    disabled={props.disabled || prohibited_parking_zones!.length === 0}
                  />
                </div>
              </div>
            </Grid>
          ) : null}

          <Grid item xs={6} spacing={5}>
            <div className={classes.inlineFlexFieldWrapper}>
              <div className={classes.inlineFlexTitleField}>
                <FieldTitle
                  title={t('titles.authorized_zones.dwell_time')}
                  tooltip={t('tooltips.authorized_zones.dwell_time')}
                />
              </div>
              <div className={classes.inlineFlexField}>
                <GeographyField
                  value={authorized_parking_zones[0]}
                  allGeographies={props.allGeographies}
                  handleChange={handleChangeRule(rule_id, 'authorized_parking_zones')}
                  disabled={props.disabled}
                  helperText={props.validation[0].getFieldErrorMsg('geographies')}
                />
              </div>
            </div>
          </Grid>
          {buffered_areas_enabled ? (
            <Grid item xs={6}>
              <div className={classes.inlineFlexFieldWrapper}>
                <div className={classes.inlineFlexTitleField}>
                  <FieldTitle
                    title={t('titles.buffered_authorized_zones.dwell_time')}
                    tooltip={t('tooltips.buffered_authorized_zones.dwell_time')}
                  />
                </div>
                <div className={classes.inlineFlexField}>
                  <GeographyField
                    value={buffered_authorized_parking_zones[0]}
                    allGeographies={props.allGeographies}
                    handleChange={handleChangeRule(rule_id, 'buffered_authorized_parking_zones')}
                    disabled={props.disabled || authorized_parking_zones!.length === 0}
                  />
                </div>
              </div>
            </Grid>
          ) : null}
        </Grid>
      </Grid>

      {SETTINGS.app.enable_timeslots && (
        <Grid container spacing={3} style={{ marginTop: '20px' }}>
          {props.rules[0].timeslots.map((timeslot, index) => {
            return (
              <Grid item xs={3} key={index}>
                <Grid container direction="column" spacing={1} style={{ marginTop: '13px' }}>
                  <Grid item component="h3">
                    {t('rule.time_slot_count', { index: index + 1 })}
                  </Grid>
                  <Grid item>
                    <TimeSlotForm
                      timeslot={timeslot}
                      handleChangeDays={handleChangeTimeslot(timeslot, index, 'days')}
                      handleChangeStartTime={handleChangeTimeslot(timeslot, index, 'start_time')}
                      handleChangeEndTime={handleChangeTimeslot(timeslot, index, 'end_time')}
                      handleRemoveTimeSlot={() => props.handleRemoveTimeSlot(index)}
                      disabled={props.isPublished && props.disabled}
                      error={isEndTimeBeforeStartTime(timeslot)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )
          })}
        </Grid>
      )}
    </>
  )
}

export default DwellTimeRule
